import type { AppRole } from "@/hooks/useAuthQueries";

/**
 * "Customer acceptance" (Kundenabnahme) capability — UNIFIED naming.
 *
 * TES-1621 GAP-1 (Owner-Entscheid 2026-07-22 #5, ADR 0008): "Kundenabnahme" was
 * modelled twice — the legacy `app_role = customer_approver` and the authoritative
 * ABAC identity `profiles.external_category = 'customer_acceptance'`. The two are now
 * consolidated onto ONE effective capability so they cannot diverge. The single
 * source of truth is the DB predicate `public.has_customer_acceptance_capability()`
 * (migration 419), where `external_category` is authoritative for external users and
 * the `customer_approver` role mirrors it for internal users. Throughout the code the
 * three terms — Kundenabnahme / customer_approver / customer_acceptance — denote the
 * SAME capability: read/limited-view defects + add acceptance evidence + own comments,
 * but NEVER manage/edit a defect's lifecycle.
 *
 * This module governs the INTERNAL (role-driven) reflection of that capability at the
 * UI boundary. In `useAuth` (useAuth.tsx:509) `isReadOnlyDefectUser = isReadOnlyDefectRole(roles)
 * || isExternalReadOnly(profile)` — i.e. this role-gate is OR-ed with the FULLY read-only
 * external categories (partner_supplier / external_auditor). The external `customer_acceptance`
 * form is a SEPARATE flag (`isExternalLimitedWrite`, "limited write": comments + evidence);
 * today an external customer_acceptance user reaches this read-only gate only through its
 * provisioned legacy `customer_approver` role. Defect edit affordances at the call-sites
 * (e.g. Defects.tsx) are gated on BOTH `!isReadOnlyDefectUser` AND `!isExternalDefectUser`
 * (the latter includes the limited-write form), so all customer-acceptance forms are covered.
 *
 * NOTE (R-SEC / ADR 0008): UI visibility is NOT the security boundary. RLS + the DB
 * capability predicate enforce this server-side. This helper only governs which controls
 * we RENDER so a read-only user is never shown an action the backend would reject anyway.
 */

/**
 * Roles that are allowed to change a defect's lifecycle (status changes:
 * start progress, resolve, verify, assign, cancel, reopen, delete, edit).
 *
 * Mirrors the real RBAC permission boundary (`defects.manage`) — the customer-acceptance
 * (Kundenabnahme / `customer_approver`) role has `manage: 'none'` for defects
 * (see UserPermissionsCard role matrix / DB role_permissions). It may only
 * view, add acceptance evidence and add comments — NEVER change status.
 */
// super_admin ist per ADR-CHK-002 (TES-1535, #425) in `admin` konsolidiert — ehemalige
// super_admins sind jetzt `admin`, daher bleibt die effektive Berechtigung unverändert.
const DEFECT_EDITING_ROLES: readonly AppRole[] = [
  "admin",
  "qualitaetsmanager",
  "tester",
  "mangelbearbeiter",
] as const;

/**
 * True when the current user is restricted to read-only defect access, i.e. holds the
 * customer-acceptance (Kundenabnahme / `customer_approver`) role WITHOUT any role that
 * grants defect lifecycle management. This is the INTERNAL-user reflection of the unified
 * customer-acceptance capability (DB SoT: `has_customer_acceptance_capability`, mig 419).
 * The external `customer_acceptance` identity form is a separate `useAuth` flag
 * (`externalRoleAccess.isExternalLimitedWrite`); see the module header for the exact wiring.
 *
 * A user that holds `customer_approver` AND e.g. `tester` is NOT read-only —
 * the broader role wins (roles are additive). An empty role set is not
 * treated as the customer-acceptance read-only case (it is simply unprivileged
 * and handled by the existing per-action ownership guards).
 *
 * SIGNATURE IS LOAD-BEARING: consumed by TES-1700 (ItemDefectsList/DefectBaseDialog) —
 * change internals only, never the `(roles) => boolean` shape.
 */
export function isReadOnlyDefectRole(roles: readonly AppRole[] | undefined | null): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  const hasCustomerApprover = roles.includes("customer_approver");
  if (!hasCustomerApprover) {
    return false;
  }
  const hasEditingRole = roles.some((role) => DEFECT_EDITING_ROLES.includes(role));
  return !hasEditingRole;
}
